import {
  useCreateEmailGroupMutation,
  useDeleteEmailGroupMutation,
  useGetEmailGroupDetailsMutation,
  useGetEmailGroupsQuery,
  useGetSnmpEmailGroupsQuery,
  useUpdateEmailGroupMutation,
} from "../../../../store/api/admin/adminApiSlice";
import { useStoreDispatch } from "../../../../store/hooks";
import { useEffect, useState } from "react";
import { setBasicDialogOpenFor, setErrorMsg, setOpenToast } from "../../../../store/helpers/helperReducers";
import { emailGroupsColumns } from "../../../../components/Shared/Table/Columns";
import { isCreateEmailSelector, selectedEmailGroupSelector } from "../../../../store/api/admin/selectors";
import { useSelector } from "react-redux";
import { IEmailSelectedGroup } from "../../../../store/api/admin/types";
import { errorMessage, titleCase } from "../../../../utils/helpers";
import { emailGroupPayload } from "../../../helpers";
import { isValidEmail } from "../../../../utils/validations";
import { encodeParam } from "../../../../store/helpers/helpers";

interface IPayload {
  [key: string]: string;
}

export const useAddEditEmailGroup = () => {
  const dispatch = useStoreDispatch();
  const selectedEmailGroup = useSelector(selectedEmailGroupSelector);
  const isCreateEmailGroup = useSelector(isCreateEmailSelector);
  const [formData, setFormData] = useState<IEmailSelectedGroup>(selectedEmailGroup);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [selectedRowIDs, setSelectedRowIDs] = useState<any[]>([]);

  const { data, isLoading, refetch } = useGetEmailGroupsQuery();
  const snmpEmailGroups = useGetSnmpEmailGroupsQuery();

  const [getSnmpGroup] = useGetEmailGroupDetailsMutation();
  const [deleteEmailGroup] = useDeleteEmailGroupMutation();

  const [createEmailGroup] = useCreateEmailGroupMutation();
  const [updateEmailGroup] = useUpdateEmailGroupMutation();

  useEffect(() => {
    setSelectedRowIDs([]);
  }, [data]);

  const getSelectedSnmpGroup = async (text: string) => {
    if (text) {
      let args = { group: text };
      try {
        let result: any;
        result = await getSnmpGroup(args);
        if (result?.error) throw result?.error;
        if (result.data?.status === 200) {
          setSelectedGroup(result?.data?.data);
        }
      } catch (error) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const handleDeleteEmailGroup = async (text: string, actionName: string) => {
    if (text) {
      try {
        let result: any;
        if (actionName.match("delete")) {
          result = await deleteEmailGroup(encodeParam(text));
          if (result?.error) throw result?.error;
          if (result.data?.status === 200) {
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `The ${text} has been deleted .`,
              })
            );
            refetch();
          }
        }
      } catch (error) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const getValue = (val: string) => {
    let checkVal = false;
    checkVal = val?.includes(",");
    return checkVal;
  };

  const updateErr = (newErr: IPayload) => {
    setErrMsg(newErr);
    dispatch(setErrorMsg(newErr));
  };

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;
    if (name === "parentKey") {
      if (!value?.trim()) {
        updateErr({ ...errMsg, [name]: `Name is required!` });
        return;
      }
      let matchCount: any = [];
      matchCount = data?.filter((el: any) => el?.parentKey?.toLowerCase() === value?.toLowerCase());
      if (isCreateEmailGroup && matchCount?.length > 0) {
        updateErr({ ...errMsg, [name]: `Invalid Name, Please use another Name. Already available in Email Group.` });
        return;
      }
    }
    if (name === "email") {
      if (!value?.trim()) {
        updateErr({ ...errMsg, [name]: `Email is required!` });
        return;
      }
      let newValue: any;
      if (getValue(value)) {
        newValue = value?.split(",");
        if (newValue?.length > 0) {
          newValue?.forEach((emailKey: any) => {
            if(emailKey?.length > 149) {
              updateErr({ [name]: `Max length should be less than 150` });
              return;
            }
            if (emailKey?.includes(" ")) {
              updateErr({ [name]: `Invalid Email, Please remove empty spaces` });
              return;
            }
            if (isValidEmail(emailKey) === null) {
              updateErr({ [name]: `Invalid Email` });
              return;
            } else return;
          });
        }
      } else {
        if(value?.length > 149) {
          updateErr({ [name]: `Max length should be less than 150` });
        }
        else if (isValidEmail(value) === null) {
          updateErr({ [name]: `Invalid Email` });
        } else updateErr({ [name]: `` });
      }
    }
  };

  const handleValidateEmailGroup = () => {
    let valid: any = true;
    if (formData?.email === "") {
      setErrMsg({ ...errMsg, email: `Email is required` });
      valid = false;
    }
    if (isCreateEmailGroup && formData?.parentKey === "") {
      setErrMsg({ ...errMsg, parentKey: `Name is required` });
      valid = false;
    }
    return valid;
  };

  const submitHandler = async () => {
    const hasErros = Object.values(errMsg).some((err) => err !== "");
    if (handleValidateEmailGroup() && !hasErros) {
      try {
        let result: any;
        if (isCreateEmailGroup) {
          result = await createEmailGroup(emailGroupPayload(formData));
        } else {
          result = await updateEmailGroup(emailGroupPayload(formData));
        }
        if (result?.error) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(setBasicDialogOpenFor(undefined));
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data has been ${isCreateEmailGroup ? "added" : "updated"} for ${titleCase(formData?.parentKey || "")}.`,
            })
          );
          refetch();
          snmpEmailGroups.refetch();
        }
      } catch (error: any) {
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  return {
    formData,
    setFormData,
    errMsg,
    setErrMsg,
    submitHandler,
    isCreateEmailGroup,
    onBlurHandler,
    data,
    isLoading,
    emailGroupsColumns,
    getSelectedSnmpGroup,
    selectedGroup,
    setSelectedGroup,
    handleDeleteEmailGroup,
    refetch,
    selectedRowIDs,
    setSelectedRowIDs,
  };
};
